import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Bentornato, ${data.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-[#16212B] p-12 overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -left-16 bottom-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
        <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-20 w-20 rounded-2xl object-cover ring-1 ring-white/10 relative z-10" />
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Il gestionale <span className="text-primary">professionale</span> per il tuo studio fisioterapico
          </h1>
          <p className="mt-5 text-slate-400 text-base leading-relaxed">
            Pazienti, appuntamenti, preventivi e incassi. Tutto in un'unica piattaforma elegante e sicura.
          </p>
        </div>
        <p className="relative z-10 text-xs text-slate-500 uppercase tracking-widest">PT-MED · Sistema Gestionale</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-12 w-12 rounded-xl object-cover" />
            <span className="font-heading text-2xl font-bold">PT-MED</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Accedi al tuo account</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">Inserisci le tue credenziali per continuare</p>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-button"
            className="w-full h-11 mb-5 font-medium"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continua con Google
          </Button>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-3 text-muted-foreground">oppure</span></div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     placeholder="nome@studio.it" data-testid="login-email-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={show ? "text" : "password"} value={password}
                       onChange={(e) => setPassword(e.target.value)} required
                       data-testid="login-password-input" className="h-11 pr-10" />
                <button type="button" onClick={() => setShow(!show)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="toggle-password-visibility">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                Password dimenticata?
              </Link>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 font-semibold">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
